import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Search as SearchIcon } from 'lucide-react';
import CourseCover from './CourseCover';
import './CourseSearch.css';

function asSearchText(value) {
  if (Array.isArray(value)) return value.map(asSearchText).join(' ');
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}

export function courseSearchTags(course) {
  const configured = course?.tags || course?.searchTags || course?.keywords;
  const explicit = Array.isArray(configured)
    ? configured.filter(tag => typeof tag === 'string' && tag.trim())
    : typeof configured === 'string'
      ? configured.split(/[,;|]/).map(tag => tag.trim()).filter(Boolean)
      : [];
  return explicit.length ? explicit : [course?.category, course?.level, course?.delivery, course?.duration].filter(Boolean);
}

export function createSearchResults(courses, query, enrollments = new Set()) {
  const term = String(query || '').trim().toLowerCase();
  if (!term) return [];
  const results = [];
  (courses || []).forEach(course => {
    const courseRef = { id: course.id, title: asSearchText(course.title) };
    const title = asSearchText(course.title);
    const description = asSearchText(course.description);
    const tags = asSearchText(courseSearchTags(course));
    const category = asSearchText(course.category);
    const combined = `${title} ${description} ${tags} ${category}`.toLowerCase();
    if (combined.includes(term)) {
      const titleMatch = title.toLowerCase().includes(term);
      results.push({
        key: `course:${course.id}`,
        type: 'course',
        course: courseRef,
        label: title,
        detail: titleMatch ? 'Course' : `Course · ${category || 'Okiru Learn'}`,
        score: title.toLowerCase().startsWith(term) ? 0 : titleMatch ? 1 : 2,
      });
    }
    if (!enrollments.has(course.id)) return;
    (course.modules || []).forEach(module => {
      (module.lessons || []).forEach(lesson => {
        const lessonTitle = asSearchText(lesson.title);
        const moduleTitle = asSearchText(module.title);
        if (`${lessonTitle} ${moduleTitle}`.toLowerCase().includes(term)) {
          results.push({
            key: `lesson:${course.id}:${lesson.id}`,
            type: 'lesson',
            course: courseRef,
            module: { id: module.id },
            lesson: { id: lesson.id },
            label: lessonTitle,
            detail: `Lesson · ${course.title}`,
            score: 3,
          });
        }
      });
    });
  });
  return results.sort((a, b) => a.score - b.score || a.label.localeCompare(b.label)).slice(0, 8);
}

export default function CourseSearch({ user, enrollments, onSelectCourse, onSelectLesson }) {
  const [courses, setCourses] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loaded, setLoaded] = useState(false);
  const rootRef = useRef(null);
  const inputRef = useRef(null);
  const listId = 'header-course-search-results';

  useEffect(() => {
    if (!user?.id) return undefined;
    let cancelled = false;
    fetch(`/api/courses?userId=${encodeURIComponent(user.id)}`)
      .then(response => response.json())
      .then(json => { if (!cancelled && json.success) setCourses(json.data || []); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const onPointerDown = event => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  const results = useMemo(() => createSearchResults(courses, query, enrollments), [courses, query, enrollments]);
  const select = result => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    if (result.type === 'lesson') onSelectLesson?.(result.course, result.module, result.lesson);
    else onSelectCourse?.(result.course);
  };
  const onKeyDown = event => {
    if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault(); setOpen(true); setActiveIndex(index => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length) {
      event.preventDefault(); setOpen(true); setActiveIndex(index => index <= 0 ? results.length - 1 : index - 1);
    } else if (event.key === 'Enter' && open && results[activeIndex]) {
      event.preventDefault(); select(results[activeIndex]);
    } else if (event.key === 'Escape') {
      setOpen(false); setActiveIndex(-1);
    }
  };

  return (
    <div className="header-course-search" ref={rootRef} role="search">
      <SearchIcon className="header-course-search-icon" size={16} aria-hidden="true" />
      <label className="sr-only" htmlFor="header-course-search-input">Search courses and lessons</label>
      <input
        ref={inputRef}
        id="header-course-search-input"
        type="search"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={Boolean(open && query.trim())}
        aria-controls={listId}
        aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
        placeholder="Search courses and lessons"
        value={query}
        onChange={event => { setQuery(event.target.value); setActiveIndex(-1); setOpen(true); }}
        onFocus={() => query.trim() && setOpen(true)}
        onKeyDown={onKeyDown}
        autoComplete="off"
      />
      {open && query.trim() && (
        <div className="header-course-search-menu" id={listId} role="listbox" aria-label="Search results">
          {results.length ? results.map((result, index) => (
            <button
              key={result.key}
              id={`${listId}-${index}`}
              type="button"
              role="option"
              aria-selected={activeIndex === index}
              className="header-course-search-result"
              onMouseEnter={() => setActiveIndex(index)}
              onClick={() => select(result)}
            >
              <CourseCover courseId={result.course.id} title={result.course.title} decorative compact className="header-course-search-cover" />
              <span className="header-course-search-result-copy">
                <span className="header-course-search-result-title">{result.label}</span>
                <span className="header-course-search-result-detail">{result.detail}</span>
              </span>
            </button>
          )) : (
            <p className="header-course-search-empty" role="status">
              {loaded ? 'No matching courses or enrolled lessons.' : 'Loading course search…'}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
